import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import Text from "~/components-shared/Text";
import Mark from "mark.js";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Card, Grid, Icon, InputText, Picture, Spinner, InputSelect } from "~/components-shared";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";
import topicSelectors from "~/store/topics/selectors";
import process from "~/assets/i/illustrations/process.svg";
import Markdown from "~/components/Markdown";
import lists from "~/lib/lists";
import { AuthoritiesEnum, CRUDActionEnum, StatusStateEnum, TopicFootprintSourceEnum } from "~/types/enums";
import TopicFootprintTarget from "~/components/TopicFootprintTarget";
import TopicFootprint from "~/components/TopicFootprint";
import TopicFootprintGroup from "~/components/TopicFootprintGroup";
import styled, { css, useTheme } from "styled-components";
import topicFootprintSelectors from "~/store/topicFootprints/selectors";
import topicFootprintActionCreators from "~/store/topicFootprints/actions";
import topicActionCreators from "~/store/topics/actions";
import { TopicActionEnum } from "~/store/topics/types";
import useAbacExt from "~/hooks/abac";
import TopicFootprintSelectionCard from "~/components/TopicFootprintSelectionCard";
import { initialFilter } from "~/store/topicFootprints/reducer";
const StyledCard = styled(Card)`
  .topic__footprint-target,
  .topic__footprint,
  .topic__footprint-group {
    border-radius: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.shape.borderRadius.primary;
}};
    transition: background-color 350ms ease-in-out;
    padding: 10px 0;

    .topic__footprint-checkbox,
    .topic__footprint-absolute-date,
    .topic__footprint-actions {
      transition: opacity 350ms ease-in-out;
      opacity: 0;
    }

    .topic__footprint-timeline:before {
      transition: background-color 350ms ease-in-out;
      background-color: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.background.secondary;
}};
    }

    &--hovered,
    &--selected {
      background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};

      .topic__footprint-checkbox {
        opacity: 1;
      }

      .topic__footprint-timeline:before {
        background-color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.background.primary;
}};
      }
    }

    &--hovered {
      .topic__footprint-absolute-date,
      .topic__footprint-actions {
        opacity: 1;
      }
    }
  }
`;
const StyledGrid = styled(Grid)`
  overflow: hidden;
  position: relative;

  ${_ref5 => {
  let {
    $loading
  } = _ref5;
  return $loading && css`
      cursor: pointer;

      &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${_ref6 => {
    let {
      theme
    } = _ref6;
    return theme.colors.background.primary;
  }};
        opacity: 0.6;
        z-index: 1;
        transition: background-color 200ms ease-out;
      }
    `;
}}
`;
const RetroButton = styled(Button)`
  position: relative;
  transform: translateX(-50%);
  margin-left: 3px;
  box-shadow: 0 0 0 4px ${_ref7 => {
  let {
    theme
  } = _ref7;
  return theme.colors.background.secondary;
}};
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const TopicFootprintsCard = _ref8 => {
  let {
    topic,
    ...rest
  } = _ref8;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const footprints = useSelector(topicFootprintSelectors.getFootprints(topic.id));
  const {
    hasAfter
  } = useSelector(topicFootprintSelectors.getPagination(topic.id));
  const status = useSelector(topicFootprintSelectors.getStatusByTopic(topic.id));
  const selectedData = useSelector(topicFootprintSelectors.getSelectedData);
  const selection = selectedData?.footprintIds || [];
  const selectionCount = selectedData?.count || [];
  const error = useSelector(topicFootprintSelectors.getErrorByTopic(topic.id));
  const filter = useSelector(topicFootprintSelectors.getFilter(topic.id));
  const targets = [...topic.targets].sort((a, b) => b.order - a.order);
  const [showExplanation, setShowExplanation] = useState(false);
  const labels = useSelector(topicSelectors.getLabels);
  const dispatch = useDispatch();
  const {
    userHasOneOf
  } = useAbacExt();
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(topicFootprintActionCreators.fetchStarted(topic.id));
    }
  }, [status, dispatch]);
  useEffect(() => {
    return () => {
      dispatch(topicFootprintActionCreators.filtered(topic.id, {
        ...initialFilter
      }));
    };
  }, []);
  useEffect(() => {
    const markInstance = new Mark(document.querySelectorAll(".search-content"));
    markInstance.unmark({
      done() {
        markInstance.mark(filter.query, {
          separateWordSearch: false
        });
      }
    });
  }, [filter.query, status]);
  const handleLoadMore = () => {
    dispatch(topicFootprintActionCreators.loadedMore(topic.id));
  };
  const handleFilter = newFilter => {
    dispatch(topicFootprintActionCreators.filtered(topic.id, {
      ...filter,
      ...newFilter
    }));
  };
  const handleRunRetro = () => {
    dispatch(topicActionCreators.actionChanged(TopicActionEnum.RUN_RETRO, topic.id));
  };
  const handleCreateFootprint = () => {
    dispatch(topicFootprintActionCreators.actionChanged(CRUDActionEnum.CREATE, null, topic.id));
  };
  const handleToggleFilterStarred = () => {
    handleFilter({
      starred: !filter.starred
    });
  };
  const handleToggleSelection = function (ids) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let sel = selection;
    if (!force) {
      const isAllSelected = ids.every(id => sel.includes(id));
      if (isAllSelected) {
        sel = sel.filter(id => !ids.includes(id));
      } else {
        const newValues = [...sel, ...ids];
        sel = newValues.filter(function (item, pos) {
          return newValues.indexOf(item) === pos;
        });
      }
    } else {
      sel = ids;
    }
    dispatch(topicFootprintActionCreators.actionChanged(null, null, {
      footprintIds: sel,
      count: sel.includes("*") ? topic.footprintCount - sel.length + 1 : sel.length
    }));
  };
  const filteredFootprints = footprints;
  const processStartDate = filteredFootprints[filteredFootprints.length - 1]?.date;
  let currentDate = moment(processStartDate);
  const checkedTargets = targets.filter(target => target.checkedAt);
  const latestCheckedIndex = targets.findIndex(target => target.id === checkedTargets[0]?.id);
  const nextAvailableTarget = checkedTargets.length ? targets[latestCheckedIndex - 1] : targets[topic.targets.length - 1];
  if (nextAvailableTarget) {
    checkedTargets.unshift(nextAvailableTarget);
  }
  const mappedTargets = checkedTargets.map(_ref9 => {
    let {
      id,
      name,
      color,
      checkedAt,
      duration
    } = _ref9;
    currentDate = currentDate.add(duration, "hours");
    let date = moment(filteredFootprints[0]?.date).add("100", "years").toISOString();
    if (checkedAt) {
      currentDate = moment(checkedAt);
      date = moment(checkedAt).toISOString();
    }
    return {
      id,
      type: "target",
      name,
      color,
      endsAt: currentDate.toISOString(),
      checkedAt,
      date
    };
  });
  let groupedFootprints = filteredFootprints;
  if (topic.groupedFootprints && !filter.query) {
    const groupedFootprintArrays = [];
    let groupIndex = -1;
    let groupName;
    filteredFootprints.forEach(footprint => {
      let newGroupName;
      switch (footprint.source) {
        case TopicFootprintSourceEnum.SLACK:
          newGroupName = `${footprint.source}|${footprint.data?.channelId}|${footprint.data?.channelName}`;
          break;
        case TopicFootprintSourceEnum.GOOGLE_CALENDAR:
          newGroupName = `${footprint.source}|${footprint.data?.title}|${footprint.data?.url}`;
          break;
        case TopicFootprintSourceEnum.NOTION:
          newGroupName = `${footprint.source}|${footprint.data?.url}`;
          break;
        case TopicFootprintSourceEnum.GOOGLE_DOCS:
        case TopicFootprintSourceEnum.GOOGLE_SHEETS:
        case TopicFootprintSourceEnum.GOOGLE_SLIDES:
          newGroupName = `${footprint.source}|${footprint.data?.title}`;
          break;
        case TopicFootprintSourceEnum.JIRA:
          newGroupName = `${footprint.source}|${footprint.data?.issueKey}`;
          break;
        case TopicFootprintSourceEnum.LINEAR:
          newGroupName = `${footprint.source}|${footprint.data?.issueKey}`;
          break;
        case TopicFootprintSourceEnum.ASANA:
          newGroupName = `${footprint.source}|${footprint.data?.issueId}`;
          break;
        case TopicFootprintSourceEnum.GOOGLE_MAIL:
          newGroupName = `${footprint.source}|${footprint.data?.threadId}|${footprint.data?.authorEmail}`;
          break;
        case TopicFootprintSourceEnum.JOYN:
          newGroupName = `${footprint.source}|${footprint.uniqueId}`;
          break;
        default:
          newGroupName = `${footprint.source}`;
      }
      if (groupName !== newGroupName) {
        groupName = newGroupName;
        groupIndex += 1;
      }
      if (groupedFootprintArrays[groupIndex]) {
        groupedFootprintArrays[groupIndex].push(footprint);
      } else {
        groupedFootprintArrays.push([footprint]);
      }
    });
    groupedFootprints = groupedFootprintArrays.map((group, index) => {
      if (group.length > 1) {
        const members = group.map(item => item.createdBy).filter((member, i, memberArray) => memberArray.findIndex(_ref10 => {
          let {
            id
          } = _ref10;
          return id === member.id;
        }) === i);
        const firstItem = group[0];
        const lastItem = group[group.length - 1];
        return {
          id: `group-${index}`,
          source: firstItem.source,
          type: "group",
          members,
          date: firstItem.date,
          isRetro: firstItem.isRetro,
          dateFrom: firstItem.date,
          dateTo: lastItem.date,
          footprints: group
        };
      }
      return group[0];
    });
  }
  const footprintsAndTargets = [...groupedFootprints, ...mappedTargets].sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
  return /*#__PURE__*/React.createElement(StyledCard, _extends({
    s: {
      p: 0
    },
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: footprints.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      center: true
    })
  }, topic.isRetroWorking && /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    title: t("TopicFootprintsCard.retroInProgress"),
    s: {
      mb: 4
    }
  }, t("TopicFootprintsCard.retroInProgressDesc")), /*#__PURE__*/React.createElement(Grid, {
    gap: 3,
    cols: 5,
    template: "auto 150px 180px 150px 3.1rem 3.1rem 3.1rem"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("TopicFootprintsCard.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    color: "translucent",
    s: {
      mb: 4
    },
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    options: labels.map(item => ({
      id: item.id,
      color: item.color,
      value: item.title,
      tip: item.description
    })),
    value: filter?.labelIds,
    onChange: value => handleFilter({
      ...filter,
      labelIds: value
    }),
    color: "translucent",
    placeholder: t("TopicFootprintsCard.allLabels"),
    withSearch: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    options: topic.contributors.map(item => ({
      id: item.id,
      value: `${item.firstName} ${item.lastName}`
    })),
    value: filter?.memberIds,
    onChange: value => handleFilter({
      ...filter,
      memberIds: value
    }),
    color: "translucent",
    placeholder: t("TopicFootprintsCard.allContributors"),
    withSearch: true,
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    options: [...topic.sources, TopicFootprintSourceEnum.JOYN].map(item => ({
      id: item,
      value: lists.topics.footprintSources.names[item]
    })),
    value: filter?.sources,
    onChange: value => handleFilter({
      ...filter,
      sources: value
    }),
    color: "translucent",
    placeholder: t("TopicFootprintsCard.allSources"),
    multiple: true
  }), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicFootprintsCard.showStarred"),
    s: {
      p: 2.3
    },
    color: filter.starred ? "translucentPrimary" : "translucent",
    onClick: handleToggleFilterStarred,
    outline: false
  }, /*#__PURE__*/React.createElement(Icon, {
    src: filter.starred ? "starFill" : "star",
    scale: 6
  })), /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("TopicFootprintsCard.showMetadata"),
    s: {
      p: 2.3
    },
    color: showExplanation ? "translucentPrimary" : "translucent",
    onClick: () => setShowExplanation(prev => !prev),
    outline: false
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "troubleshoot",
    scale: 6
  })), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("common.actions.create"),
    s: {
      p: 2.3
    },
    color: "translucent",
    onClick: handleCreateFootprint,
    outline: false
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    scale: 6
  }))), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 4
    },
    fullWidth: true
  }, error), filteredFootprints.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    $loading: status === StatusStateEnum.LOADING,
    cols: 1,
    template: "1fr",
    gap: 3,
    fullWidth: true,
    s: {
      mb: 6,
      pt: 2
    }
  }, footprintsAndTargets.map(footprint => {
    switch (footprint.type) {
      case "target":
        return /*#__PURE__*/React.createElement(TopicFootprintTarget, {
          key: footprint.id,
          topicId: topic.id,
          target: footprint
        });
      case "group":
        return /*#__PURE__*/React.createElement(TopicFootprintGroup, {
          key: footprint.id,
          topicId: topic.id,
          group: footprint,
          selection: selection,
          setSelection: handleToggleSelection
        });
      default:
        return /*#__PURE__*/React.createElement(TopicFootprint, {
          key: footprint.id,
          topicId: topic.id,
          footprint: footprint,
          selection: selection,
          showExplanation: showExplanation,
          setSelection: handleToggleSelection
        });
    }
  }), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Grid, {
    cols: 4,
    gap: 6,
    template: "200px 6px 3.4rem 1fr",
    fullWidth: true,
    s: {
      mb: 1
    }
  }, /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(RetroButton, {
    rounded: true,
    s: {
      p: 2
    },
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-place": "right",
    "data-tooltip-content": t("TopicFootprintsCard.runRetro"),
    onClick: handleRunRetro,
    color: "primaryGrey"
  }, /*#__PURE__*/React.createElement(Icon, {
    scale: 6,
    src: "searchHistory",
    color: "primary"
  }))))) : status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : /*#__PURE__*/React.createElement(Grid, {
    cols: 1,
    justify: "center",
    s: {
      mt: 10
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    src: process,
    width: "300px",
    height: "200px",
    border: "squared",
    backgroundSize: "contain"
  }), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    weight: "semiBold",
    display: "block",
    s: {
      mt: 5
    }
  }, t("TopicFootprintsCard.noFootprints")), /*#__PURE__*/React.createElement(Markdown, {
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    display: "block",
    s: {
      mt: 3,
      mb: 5
    },
    style: {
      maxWidth: "500px"
    },
    align: "center",
    text: t("TopicFootprintsCard.noFootprintsInfo")
  }), userHasOneOf([AuthoritiesEnum.TOPICS_WRITE]) && /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "searchHistory",
      scale: 5,
      s: {
        mr: 2
      }
    }),
    onClick: handleRunRetro,
    s: {
      mb: 3
    },
    outline: true
  }, t("TopicFootprintsCard.runRetro")))), !!selection.length && /*#__PURE__*/React.createElement(TopicFootprintSelectionCard, {
    topic: topic,
    selection: selection,
    selectionCount: selectionCount,
    setSelection: handleToggleSelection
  }));
};
export default TopicFootprintsCard;